/**
 * RxDB schemas for the field-zone offline collections.
 *
 * Two collections:
 *  - tasks     : local mirror of server tasks (assigned to / created by this user)
 *  - expenses  : user-created expense claims waiting for online receipt sync
 *  - outbox    : pending write operations queued while offline
 *
 * Primary keys MUST be strings in RxDB. We use `rxdb_id`:
 *   - server tasks  → "task_<server_id>"
 *   - offline-only  → "temp_<uuid>" (replaced after a successful create sync)
 */
import type { RxJsonSchema } from 'rxdb'

// ── Task document ─────────────────────────────────────────────────────────────

export interface TaskDocument {
  rxdb_id: string           // primary key
  local_id: number | null   // negative client id until server_id is known
  server_id: number | null  // null until the create op syncs
  // The server's public_id (ADR 0007) — null until the create op syncs, same
  // as server_id. update_task outbox ops replay against this, not server_id:
  // the PATCH /api/work/tasks/:id route resolves by public_id, not the
  // internal int (plan 021 step 7).
  public_id: string | null
  org_id: number
  user_id: number           // active user that owns this local mirror
  title: string
  description: string
  priority: 'critical' | 'high' | 'normal' | 'low'
  status: 'not_started' | 'in_progress' | 'under_review' | 'completed'
  due_date: string | null
  assigned_to: number | null
  project_id: number | null
  phase_id: number | null
  created_by: number
  labels: string[]
  approval_status: string | null
  approval_notes: string | null
  created_at: string
  updated_at: string
  // sync meta
  dirty: boolean  // has unsent local changes
  sync_status: 'synced' | 'pending_sync' | 'sync_failed'
  sync_error: string | null
}

export const taskSchema: RxJsonSchema<TaskDocument> = {
  version: 4,
  primaryKey: 'rxdb_id',
  type: 'object',
  properties: {
    rxdb_id: { type: 'string', maxLength: 64 },
    local_id: { type: ['number', 'null'] },
    server_id: { type: ['number', 'null'] },
    public_id: { type: ['string', 'null'] },
    org_id: { type: 'number' },
    user_id: { type: 'number' },
    title: { type: 'string' },
    description: { type: 'string' },
    priority: { type: 'string' },
    status: { type: 'string' },
    due_date: { type: ['string', 'null'] },
    assigned_to: { type: ['number', 'null'] },
    project_id: { type: ['number', 'null'] },
    phase_id: { type: ['number', 'null'] },
    created_by: { type: 'number' },
    labels: { type: 'array', items: { type: 'string' } },
    approval_status: { type: ['string', 'null'] },
    approval_notes: { type: ['string', 'null'] },
    created_at: { type: 'string' },
    updated_at: { type: 'string' },
    dirty: { type: 'boolean' },
    sync_status: { type: 'string' },
    sync_error: { type: ['string', 'null'] },
  },
  required: ['rxdb_id', 'title', 'status', 'priority', 'org_id', 'user_id', 'created_by', 'dirty', 'sync_status'],
  // These collections are small and all current reads are full scans or
  // primary-key lookups. Avoid custom indexes here: nullable IDs, booleans,
  // and unbounded strings require extra RxDB constraints and can prevent the
  // entire database from opening in development.
  indexes: [],
}

// ── Scoped offline expense claim ────────────────────────────────────────────

export interface OfflineReceiptMeta {
  attachment_id: string
  op_id: string
  file_name: string
  content_type: string
  size_bytes: number
  synced: boolean
}

export interface ExpenseDocument {
  rxdb_id: string
  local_id: number
  server_id: number | null
  org_id: number
  user_id: number
  payload: string
  receipts: OfflineReceiptMeta[]
  sync_status: 'pending_sync' | 'syncing' | 'sync_failed'
  last_error: string | null
  created_at: string
  updated_at: string
  dirty: boolean
}

export const expenseSchema: RxJsonSchema<ExpenseDocument> = {
  version: 2,
  primaryKey: 'rxdb_id',
  type: 'object',
  attachments: {},
  properties: {
    rxdb_id: { type: 'string', maxLength: 96 },
    local_id: { type: 'number' },
    server_id: { type: ['number', 'null'] },
    org_id: { type: 'number' },
    user_id: { type: 'number' },
    payload: { type: 'string' },
    receipts: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          attachment_id: { type: 'string' },
          op_id: { type: 'string' },
          file_name: { type: 'string' },
          content_type: { type: 'string' },
          size_bytes: { type: 'number' },
          synced: { type: 'boolean' },
        },
        required: ['attachment_id', 'op_id', 'file_name', 'content_type', 'size_bytes', 'synced'],
      },
    },
    sync_status: { type: 'string' },
    last_error: { type: ['string', 'null'] },
    created_at: { type: 'string' },
    updated_at: { type: 'string' },
    dirty: { type: 'boolean' },
  },
  required: ['rxdb_id', 'local_id', 'org_id', 'user_id', 'payload', 'receipts', 'sync_status', 'dirty', 'created_at', 'updated_at'],
  indexes: [],
}

// ── Outbox document ───────────────────────────────────────────────────────────

export type OutboxOpType = 'create_task' | 'update_task' | 'submit_task' | 'approve_task' | 'reject_task' | 'create_expense'

export interface OutboxDocument {
  op_id: string           // primary key (UUID, sent as X-Op-Id to server)
  op_type: OutboxOpType
  rxdb_id: string         // local task reference
  server_id: number | null
  payload: string         // JSON-serialised body
  attempts: number
  last_attempt_at: string | null
  last_error: string | null
  created_at: string
}

export const outboxSchema: RxJsonSchema<OutboxDocument> = {
  version: 2,
  primaryKey: 'op_id',
  type: 'object',
  properties: {
    op_id: { type: 'string', maxLength: 64 },
    op_type: { type: 'string' },
    rxdb_id: { type: 'string' },
    server_id: { type: ['number', 'null'] },
    payload: { type: 'string' },
    attempts: { type: 'number' },
    last_attempt_at: { type: ['string', 'null'] },
    last_error: { type: ['string', 'null'] },
    created_at: { type: 'string' },
  },
  required: ['op_id', 'op_type', 'rxdb_id', 'payload', 'attempts', 'last_error', 'created_at'],
  indexes: [],
}
